import { useCallback, useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import { useRefreshOn } from '../../lib/resourceRefresh';
import type { GenerationRequestStatus, GenerationRequestSummaryRead } from '../../types/api';
import { fetchGenerationRequests } from '../generation/generationApi';

/** One page of history. The backend caps `limit` at 100; 20 is a screenful. */
export const HISTORY_PAGE_SIZE = 20;

/** Nothing filtered. Stable, so the default does not churn the effect. */
const ALL_STATUSES: readonly GenerationRequestStatus[] = [];

export interface RequestHistoryOptions {
  /** Only these states (repeated `status` param). Empty or absent = all. */
  status?: readonly GenerationRequestStatus[];
  /** Rows per page; defaults to {@link HISTORY_PAGE_SIZE}. */
  pageSize?: number;
}

export interface RequestHistoryResult {
  requests: GenerationRequestSummaryRead[];
  /** The first page is in flight and there is nothing to show yet. */
  isLoading: boolean;
  /** A later page is in flight; the rows already on screen stay put. */
  isLoadingMore: boolean;
  error: string | null;
  /** The last page came back full, so there is probably another one. */
  hasMore: boolean;
  loadMore: () => void;
  /** Re-runs the page that failed, keeping everything already loaded. */
  retry: () => void;
}

/** Result of the last completed page load, tagged with what it was loaded for. */
interface LoadedHistory {
  /** Business, filter and page size together: a change to any is a new list. */
  key: string;
  offset: number;
  nonce: number;
  requests: GenerationRequestSummaryRead[];
  hasMore: boolean;
  error: string | null;
}

/** Where paging has got to, tagged with the list it belongs to. */
interface Paging {
  key: string;
  offset: number;
}

/** Appends a page: ids already held take the fresher row in place, new ids go last. */
function merge(
  previous: GenerationRequestSummaryRead[],
  page: GenerationRequestSummaryRead[],
): GenerationRequestSummaryRead[] {
  const fresh = new Map(page.map((request) => [request.id, request]));
  const kept = previous.map((request) => fresh.get(request.id) ?? request);
  const seen = new Set(previous.map((request) => request.id));
  return [...kept, ...page.filter((request) => !seen.has(request.id))];
}

/**
 * The paginated request log, optionally narrowed to some statuses.
 *
 * Same shape as `useGenerationRequest`: the result is stored tagged with the
 * list it was read for (business + filter + page size) and with the page it
 * answers, and the visible state is derived during render — so nothing is
 * written to state synchronously in the effect, another business's rows can
 * never flash on screen, and switching the filter starts again at offset 0
 * without an effect to reset it.
 *
 * Offsets accumulate rather than replacing, because a log reads downwards. That
 * means a request created while the user is paging can shift a row from one page
 * into the next; ids are deduped for exactly that, and the reverse (a row missed
 * because it moved *up* past a boundary) is harmless on a list that is read, not
 * reconciled.
 *
 * A deploy from before the status filter ignores the parameter and answers with
 * every state, so the filter is applied again here. "Is there more" still reads
 * the raw page, so paging stays honest either way.
 */
export function useRequestHistory(
  businessId: string | null,
  options: RequestHistoryOptions = {},
): RequestHistoryResult {
  const statuses = options.status ?? ALL_STATUSES;
  const pageSize = options.pageSize ?? HISTORY_PAGE_SIZE;
  const statusKey = statuses.join(',');
  // Re-derived from the joined key so a caller's fresh array literal each
  // render is the same filter, not a new one.
  const wanted = useMemo(
    () => (statusKey ? (statusKey.split(',') as GenerationRequestStatus[]) : []),
    [statusKey],
  );
  const key = `${businessId ?? ''}|${statusKey}|${pageSize}`;

  const [loaded, setLoaded] = useState<LoadedHistory | null>(null);
  const [paging, setPaging] = useState<Paging>({ key, offset: 0 });
  const [nonce, setNonce] = useState(0);

  // A new list starts at the top. Derived, not reset in an effect.
  const offset = paging.key === key ? paging.offset : 0;

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const page = await fetchGenerationRequests(
          businessId,
          { limit: pageSize, offset, status: wanted },
          controller.signal,
        );
        if (cancelled) return;
        const rows =
          wanted.length > 0 ? page.filter((request) => wanted.includes(request.status)) : page;
        setLoaded((previous) => {
          const isContinuation = previous !== null && previous.key === key && offset > 0;
          return {
            key,
            offset,
            nonce,
            requests: isContinuation ? merge(previous.requests, rows) : rows,
            hasMore: page.length === pageSize,
            error: null,
          };
        });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded((previous) => {
          const kept = previous !== null && previous.key === key ? previous : null;
          return {
            key,
            offset,
            nonce,
            requests: kept?.requests ?? [],
            // Held at what the last good page said, so a retry asks for the page
            // that failed rather than skipping past it.
            hasMore: kept?.hasMore ?? false,
            error: getErrorMessage(caught, 'Could not load your history.'),
          };
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, key, offset, nonce, pageSize, wanted]);

  const isCurrentList = loaded !== null && loaded.key === key;
  const isCurrentPage = isCurrentList && loaded.offset === offset && loaded.nonce === nonce;
  const isFetching = businessId !== null && !isCurrentPage;

  const loadMore = useCallback(() => {
    if (isFetching || loaded === null || !isCurrentPage || !loaded.hasMore) return;
    setPaging({ key, offset: offset + pageSize });
  }, [isCurrentPage, isFetching, key, loaded, offset, pageSize]);

  const retry = useCallback(() => setNonce((value) => value + 1), []);

  // A run changed elsewhere (the assistant, or an undo): re-read the page in
  // view. The rows on screen stay put until it lands.
  useRefreshOn(businessId, 'runs', retry);

  // A re-read of a list already on screen is not "nothing to show yet".
  const isRereading =
    isCurrentList && loaded.offset === offset && loaded.error === null && loaded.nonce !== nonce;

  return {
    // Rows already loaded stay visible while the next page arrives.
    requests: isCurrentList ? loaded.requests : [],
    isLoading: isFetching && offset === 0 && !isRereading,
    isLoadingMore: isFetching && offset > 0,
    error: isCurrentPage ? loaded.error : null,
    hasMore: isCurrentPage && loaded.hasMore,
    loadMore,
    retry,
  };
}
